// OrderConfirmation — post-checkout success that SELLS (redesign Task 17,
// F24/F25/O8): success hero, Bill to (with the masked payment summary under
// it, F24) beside Ship to, items with pictures in the cart-line layout
// (ruling S5=A, via the shared ItemThumb), totals with the R5 zero-shipping
// label, and the promotional block ("What happens next" + a Refer & Save CTA
// + "Continue shopping").
//
// DATA (plan, Task 17): the confirmation fetches GET /me/orders/{orderId} and
// the frozen snapshot is the SINGLE SOURCE — no client checkout state is
// reused for the addresses/items/totals. Only the hero (order number +
// confirmation email) comes from the checkout response, because the plan
// requires it to survive a fetch failure: the failure branch still renders the
// hero + order number with a "View order" affordance. A loading skeleton shows
// while the fetch is in flight.
//
// RESTYLE NOTE (rule 104): the pre-Task-17 view was a centered card with
// "Order Confirmed" + two buttons ("View Order" / "Continue Shopping"). The
// View-order control now lives ONLY in the failure branch; on success the
// snapshot data is inline and the promotional block owns the exits. The route
// carries orderId (CheckoutFlow passes order_id from the checkout response).
//
// a11y: the success EmbedBanner self-announces role="status" (polite). It is
// the single status node — the loading skeleton is aria-hidden decoration (the
// banner has already announced the placement), so the view never carries two
// status roles (the test asserts exactly one getByRole("status")).
//
// Inline styles only — no Tailwind. Themed via useEmbedTokens().

import { useEffect, useState } from "react";
import {
  useEmbedTokens,
  EmbedCard,
  EmbedBanner,
  EmbedHeading,
  EmbedButton,
  EmbedText,
} from "../../embed-shared/primitives";
import { embedApiFetch } from "../../embed-shared/api-client";
import { formatCurrency } from "../../embed-shared/currency";
import type { OrderDetail, OrderAddressSnapshot, OrderSummary } from "../../embed-shared/types";
import { useStorefrontConfig } from "./storefront-config";
import { detailInsetStyle, useViewportWidth } from "./detail-inset";
import { ItemThumb } from "./ItemThumb";
import { TAX_AT_BILLING_VALUE, TOTAL_BEFORE_TAX_LABEL, isTaxPending } from "../../embed-shared/tax-display";

export interface OrderConfirmationProps {
  /** The placed order's id — drives the GET /me/orders/{orderId} read. */
  orderId: string;
  /** From the checkout response; rendered in the hero even if the fetch fails. */
  orderNumber: string;
  /** From the checkout response. No longer displayed: the storefront sends no
   *  order email yet, so the confirmation must not promise one (B3). Kept on
   *  the contract so the checkout caller needs no change. */
  confirmationEmail: string;
  /** Failure-branch affordance (route to the order/account). */
  onViewOrder?: () => void;
  /** Promotional block's exit back to the catalog. */
  onContinueShopping?: () => void;
  /** Refer & Save CTA (routes into the account's Refer & Save section). */
  onReferShare?: () => void;
}

type FetchState =
  | { status: "loading" }
  | { status: "failed" }
  | { status: "ready"; detail: OrderDetail };

/** The O8 promotional timeline. OPERATOR-REVIEW DRAFT copy — every string in
 *  this file is collected in the task report for review (Task 10 treatment).
 *  Constraints honoured: no "free", no "Aldelo Cloud", no discount claims, no
 *  delivery-date promises (bank-transfer confirmation timing is deliberately
 *  unstated). */
const NEXT_STEPS: ReadonlyArray<{ title: string; body: string }> = [
  { title: "Order received", body: "Your order is in. It appears in your account's order list now." },
  { title: "Setup and billing", body: "Setup and billing are arranged with Aldelo separately." },
  { title: "Preparation", body: "Your items are prepared and checked." },
];

function SectionHeading({ children }: { children: string }) {
  const t = useEmbedTokens();
  return (
    <h4
      style={{
        fontSize: t.fontSize.sm,
        fontWeight: 600,
        color: t.colors.textSecondary,
        margin: `0 0 ${t.spacing.sm}px 0`,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {children}
    </h4>
  );
}

/**
 * One address block (F5) — same anatomy as OrderDetailView's. `null` snapshot
 * = the order predates migration 124: the block still renders, as "Not
 * recorded". An undefined key (pre-API-4 server) never reaches here — the
 * caller omits the whole block (D5).
 */
function AddressBlock({ heading, address }: { heading: string; address: OrderAddressSnapshot | null }) {
  return (
    <div style={{ flex: "1 1 260px", minWidth: 0 }}>
      <SectionHeading>{heading}</SectionHeading>
      {address === null ? (
        <EmbedText tone="secondary">Not recorded</EmbedText>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <EmbedText>
            {address.line1}
            {address.line2 ? `, ${address.line2}` : ""}
          </EmbedText>
          <EmbedText tone="secondary">
            {address.city}, {address.state} {address.postal_code}
          </EmbedText>
        </div>
      )}
    </div>
  );
}

export function OrderConfirmation({
  orderId,
  orderNumber,
  onViewOrder,
  onContinueShopping,
  onReferShare,
}: OrderConfirmationProps) {
  const t = useEmbedTokens();
  const viewportWidth = useViewportWidth();
  // TD-308/D6 + R5: the $0 shipping value renders through the operator's
  // configured label (default "$0.00", migration 144), in the neutral text
  // token — a green $0.00 would read as a promotion (Task 13's ruling).
  const { freeShippingLabel } = useStorefrontConfig();

  const [state, setState] = useState<FetchState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    embedApiFetch<OrderDetail>(`/api/v1/marketplace/me/orders/${orderId}`)
      .then((data) => {
        if (cancelled) return;
        // A 2xx that unwraps to something without an order payload (the
        // client yields null/undefined when the envelope carries no data) is the
        // not-found shape — degrade to the failure branch, never crash on
        // detail.order (same read as AccountOrderDetail).
        setState(data && data.order ? { status: "ready", detail: data } : { status: "failed" });
      })
      .catch(() => {
        // The server message is never shown here — one sentence + View order,
        // not an error transcript (AccountOrderDetail's precedent).
        if (!cancelled) setState({ status: "failed" });
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const order: OrderSummary | null = state.status === "ready" ? state.detail.order : null;
  const lines = state.status === "ready" ? state.detail.lines : [];

  return (
    <div
      style={{
        // Operator item (a), 2026-08-16: same detail-view inset as the PDP
        // and cart — 15%/15% at >=900px against the full-width catalog.
        ...detailInsetStyle(t, viewportWidth),
        display: "flex",
        flexDirection: "column",
        gap: t.spacing.lg,
      }}
    >
      {/* 1. Success hero (F25) — icon, heading, order number, confirmation
          email. Route state, rendered in every fetch state. */}
      <EmbedCard padding={t.spacing.xl}>
        <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
          {/* Success banner — the single role="status" announcement node. */}
          <EmbedBanner tone="success">
            <svg
              aria-hidden="true"
              focusable="false"
              width={18}
              height={18}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flex: "0 0 auto" }}
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <EmbedHeading level={1}>Thanks — your order is placed</EmbedHeading>
          </EmbedBanner>

          <p style={{ fontSize: t.fontSize.md, color: t.colors.text, margin: 0 }}>
            Order <strong>#{orderNumber}</strong>
          </p>

          {/* B3: true today. The storefront sends no order email; billing
              happens later, so say what actually happens next. */}
          <p style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary, margin: 0 }}>
            Your order is received. Setup and billing are arranged with Aldelo separately.
            It appears in your account&apos;s order list.
          </p>
        </div>
      </EmbedCard>

      {/* Loading skeleton — aria-hidden decoration (the banner already
          announced); never a second role="status". */}
      {state.status === "loading" && (
        <div aria-hidden="true" data-testid="confirmation-skeleton" style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
          <div style={{ height: 72, borderRadius: t.borderRadius, background: t.colors.surface }} />
          <div style={{ height: 20, width: "40%", borderRadius: t.borderRadius, background: t.colors.surface }} />
          <div style={{ height: 64, borderRadius: t.borderRadius, background: t.colors.surface }} />
          <div style={{ height: 64, borderRadius: t.borderRadius, background: t.colors.surface }} />
          <div style={{ height: 120, borderRadius: t.borderRadius, background: t.colors.surface }} />
        </div>
      )}

      {/* Fetch failure — the hero and order number above still render; this
          branch adds the "View order" affordance (plan, Task 17). */}
      {state.status === "failed" && (
        <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md, alignItems: "flex-start" }}>
          <EmbedText tone="secondary">We could not load the rest of your order right now.</EmbedText>
          {onViewOrder && (
            <EmbedButton type="button" variant="secondary" onClick={onViewOrder}>
              View order
            </EmbedButton>
          )}
        </div>
      )}

      {/* 2-4. Snapshot sections (F24/F5/S5/R5) — only from the fetched
          envelope, never from client checkout state. */}
      {state.status === "ready" && order && (
        <>
          {/* Bill to (payment summary UNDER it, F24) beside Ship to —
              stacking at phone width (flexWrap). Block-scoped D5: an absent
              key omits the block; a null one renders "Not recorded" inside
              it. Same semantics as OrderDetailView. */}
          {(order.bill_to !== undefined || order.ship_to !== undefined) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: t.spacing.lg }}>
              {order.bill_to !== undefined && (
                <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                  <AddressBlock heading="Bill to" address={order.bill_to} />
                  {order.payment && (
                    <div style={{ marginTop: t.spacing.xs }}>
                      <EmbedText tone="secondary">{order.payment.label}</EmbedText>
                    </div>
                  )}
                </div>
              )}
              {order.ship_to !== undefined && <AddressBlock heading="Ship to" address={order.ship_to} />}
            </div>
          )}

          {/* 3. Items — cart-line layout with pictures (S5=A): a line with an
              image renders it (alt = product name); a line without renders
              ItemThumb's labelled placeholder tile. */}
          <div>
            <SectionHeading>Items</SectionHeading>
            {lines.map((line) => (
              <div
                key={line.id}
                style={{
                  display: "flex",
                  gap: t.spacing.md,
                  padding: `${t.spacing.sm}px 0`,
                  borderBottom: `1px solid ${t.colors.cardBorder}`,
                  alignItems: "center",
                }}
              >
                <ItemThumb name={line.product_name} imageUrl={line.image_url ?? null} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: t.fontSize.sm, fontWeight: 500, color: t.colors.text, overflowWrap: "anywhere" }}>
                    {line.product_name}
                  </span>
                  <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary, marginLeft: t.spacing.sm }}>
                    x{line.quantity}
                  </span>
                </div>
                <span style={{ fontSize: t.fontSize.sm, fontWeight: 600, color: t.colors.text, fontVariantNumeric: "tabular-nums" }}>
                  {formatCurrency(line.line_total)}
                </span>
              </div>
            ))}
          </div>

          {/* 4. Totals — same rows as OrderDetailView; the $0 shipping value
              renders the operator's configured label (R5/migration 144). */}
          <div style={{ borderTop: `1px solid ${t.colors.cardBorder}`, paddingTop: t.spacing.sm }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: `${t.spacing.xs}px 0` }}>
              <EmbedText tone="secondary">Subtotal</EmbedText>
              <span style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary, fontVariantNumeric: "tabular-nums", lineHeight: t.lineHeight.normal }}>{formatCurrency(order.subtotal)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: `${t.spacing.xs}px 0` }}>
              <EmbedText tone="secondary">Tax</EmbedText>
              <span style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary, fontVariantNumeric: "tabular-nums", lineHeight: t.lineHeight.normal }}>{isTaxPending(order.tax_amount) ? TAX_AT_BILLING_VALUE : formatCurrency(order.tax_amount)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: `${t.spacing.xs}px 0` }}>
              <EmbedText tone="secondary">Shipping</EmbedText>
              {order.shipping === 0 ? (
                <span style={{ color: t.colors.text, fontWeight: 600, fontSize: t.fontSize.sm, fontVariantNumeric: "tabular-nums" }}>{freeShippingLabel}</span>
              ) : (
                <span style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary, fontVariantNumeric: "tabular-nums", lineHeight: t.lineHeight.normal }}>{formatCurrency(order.shipping)}</span>
              )}
            </div>
            {order.discount_amount > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", padding: `${t.spacing.xs}px 0`, color: t.colors.successGreen }}>
                <span style={{ fontSize: t.fontSize.sm, color: t.colors.successGreen }}>
                  {order.promo_code ? `Promo (${order.promo_code})` : "Discount"}
                </span>
                <span style={{ fontSize: t.fontSize.sm, color: t.colors.successGreen, fontVariantNumeric: "tabular-nums" }}>
                  -{formatCurrency(order.discount_amount)}
                </span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: t.fontSize.md, fontWeight: 700, color: t.colors.text, padding: `${t.spacing.sm}px 0`, borderTop: `1px solid ${t.colors.cardBorder}`, marginTop: t.spacing.xs }}>
              <span>{isTaxPending(order.tax_amount) ? TOTAL_BEFORE_TAX_LABEL : "Total"}</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatCurrency(order.total)}</span>
            </div>
          </div>
        </>
      )}

      {/* 5. Promotional block (O8/F25) — what happens next + the Refer & Save
          CTA + the way back to the catalog. Copy is OPERATOR-REVIEW DRAFT. */}
      <EmbedCard padding={t.spacing.lg}>
        <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
          <div>
            <SectionHeading>What happens next</SectionHeading>
            <ol
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "flex",
                flexDirection: "column",
                gap: t.spacing.md,
              }}
            >
              {NEXT_STEPS.map((step, index) => (
                <li key={step.title} style={{ display: "flex", gap: t.spacing.md, alignItems: "flex-start" }}>
                  <span
                    aria-hidden="true"
                    style={{
                      flex: "0 0 auto",
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: t.colors.surface,
                      border: `1px solid ${t.colors.cardBorder}`,
                      color: t.colors.textSecondary,
                      fontSize: t.fontSize.xs,
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {index + 1}
                  </span>
                  <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <EmbedText>{step.title}</EmbedText>
                    <EmbedText tone="secondary">{step.body}</EmbedText>
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {(onReferShare || onContinueShopping) && (
            <div
              style={{
                borderTop: `1px solid ${t.colors.cardBorder}`,
                paddingTop: t.spacing.md,
                display: "flex",
                flexWrap: "wrap",
                gap: t.spacing.md,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                {/* A styled call-out, not a document heading — the section's
                    h4 above is the only heading here (keeps the outline
                    linear under the hero's h1). */}
                <span style={{ fontSize: t.fontSize.md, fontWeight: 600, color: t.colors.text }}>Refer &amp; Save</span>
                <EmbedText tone="secondary">Share your success, save on processing.</EmbedText>
              </div>
              <div style={{ display: "flex", gap: t.spacing.md, flexWrap: "wrap" }}>
                {onReferShare && (
                  <EmbedButton type="button" variant="primary" onClick={onReferShare}>
                    Refer a store
                  </EmbedButton>
                )}
                {onContinueShopping && (
                  <EmbedButton type="button" variant="secondary" onClick={onContinueShopping}>
                    Continue shopping
                  </EmbedButton>
                )}
              </div>
            </div>
          )}
        </div>
      </EmbedCard>
    </div>
  );
}
